import { Button } from '@studio/design-system/button';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { Spinner } from '@studio/design-system/spinner';
import type { Device } from '@studio/models/device';
import type { PTZ } from '@studio/models/ptz';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { DPad } from '#~/control/components/d-pad/index.js';
import { move } from '#~/control/movement/commands/index.js';
import { onboardingContextFor, useOnboardingTracker } from '../hooks/use-onboarding-tracker.js';
import { Sel, STagNew, STagType } from '#~/stag/tag.js';
import { Viewer } from '#~/video/components/viewer/index.js';
import { FunnelShell } from '../components/funnel-shell.js';
import { MAGIC_MOMENT_FALLBACK_MS, MAGIC_MOMENT_POLL_MS } from '../constants.js';
import {
  isRealMovement,
  isVideoFailed,
  selectCoachMark,
  shouldReportNoVideo
} from '../magic-moment-decisions.js';
import { createPlaybackSampler } from '../video-readiness.js';
import type { CameraSummary } from './types.js';
import { useStepView } from '../hooks/use-step-view.js';

/**
 * How the magic moment ended. `videoVerified` is true only when THIS screen's
 * viewer had frames arriving at the last poll. When the 120s wait ends without
 * it, the screen reports `onboarding_failed`; the gate does not branch on it.
 */
export interface MagicMomentOutcome {
  readonly videoVerified: boolean;
}

interface ScreenMagicMomentProps {
  readonly deviceId: Device['id'];
  readonly config: CameraSummary;
  /**
   * Does this camera have controls the user asked us to use? False for a fixed
   * camera and for anyone who switched controls off on the model step, and the
   * D-pad is then not rendered at all.
   */
  readonly controllable: boolean;
  readonly onComplete: (outcome: MagicMomentOutcome) => void;
}

// The magic moment: the user's own camera, live, and one PTZ move to prove it
// is theirs to drive.
//
// Every verdict here is decided in magic-moment-decisions.ts. What stays in
// this file is the wiring, and two pieces of it are load-bearing enough to say
// out loud:
//
// 1. Liveness is the last poll's frame-advancement sample, not a sticky
//    decoded-frame level. A mounted last frame keeps readyState/width and
//    used to exit as verified. Completion peeks that sample so it cannot
//    consume the poll or disagree with the LIVE badge.
// 2. The probe is scoped to this screen's own container. A document-wide query
//    also matches the Studio dashboard's video tiles behind the blurred
//    backdrop, which would satisfy the readiness check using a completely
//    different camera.
//
// Per rules: code-comments-required, reference-rules-in-comments,
// ai-code-requires-tests, ui-architecture (ai.knowledge/global).
export const ScreenMagicMoment = ({
  deviceId,
  config,
  controllable,
  onComplete
}: ScreenMagicMomentProps): ReactElement => {
  useStepView('magic-moment');

  const [hasSentMovement, setHasSentMovement] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [waitExpired, setWaitExpired] = useState(false);
  // Bumped by "Try again": remounts Viewer for a fresh LiveKit subscription
  // rather than giving the same dead stream more time.
  const [retrySignal, setRetrySignal] = useState(0);

  const videoContainerRef = useRef<HTMLDivElement>(null);
  const samplerRef = useRef(createPlaybackSampler());
  const celebrationTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Tracked separately from the celebration timer so unmount can cancel it.
  // Without this, onComplete still fires ~1.2s after the celebration even when
  // the user has already left, invoking it twice.
  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const firstStreamFiredRef = useRef(false);
  const noVideoReportedRef = useRef(false);
  const waitExpiredRef = useRef(false);
  waitExpiredRef.current = waitExpired;

  const tracker = useOnboardingTracker();
  // The tracker is memoized on `track`, which changes identity once when the
  // analytics provider finishes initialising. `finish` must not be rebuilt on
  // that flip — the fallback timer below is armed from it exactly once per
  // attempt — so it reads the tracker from here instead of a dependency list.
  const trackerRef = useRef(tracker);
  trackerRef.current = tracker;
  // The caller passes a fresh closure on every render and this screen re-renders
  // once a second while video is live.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  /** Last poll: did a frame advance in this screen's viewer? Peek, do not sample. */
  const isVideoLiveNow = useCallback((): boolean => samplerRef.current.peek(), []);

  // Polls for the screen's life. First tick stores a baseline; Live turns on
  // once a later tick sees totalVideoFrames increase and stays on while frames
  // keep arriving within the sampler's live window.
  useEffect(() => {
    const interval = setInterval(() => {
      const container = videoContainerRef.current;
      if (container === null) return;
      const live = samplerRef.current.observe(container);
      // Commit in this task: a click queued right behind the poll must see
      // the badge that matches what finish() peeks.
      flushSync(() => setVideoReady(live));
    }, MAGIC_MOMENT_POLL_MS);

    return () => clearInterval(interval);
  }, [retrySignal]);

  useEffect(() => {
    if (!videoReady || firstStreamFiredRef.current) return;
    firstStreamFiredRef.current = true;
    // NO first-stream event. ENG-4305 lists `onboarding_first_stream` as
    // superseded — "first stream = the first streaming-success for a device_id
    // after its device_add, deduped" — so a wizard-specific twin is exactly the
    // duplication that reconciliation removes.
    //
    // The shared room observer emits `media_stream_succeeded` when this device
    // becomes ready. The wizard keeps no second copy. The shared event sits
    // between connection-test success and this screen's completion.
  }, [videoReady]);

  // The single exit, so every way out — Done, Escape, the celebration, the
  // fallback window — reports the same verdict. Reads only refs, so its identity
  // is stable and the fallback timer below is armed once per attempt.
  const finish = useCallback((): void => {
    // Peek the last poll. A second observe here would store the current
    // frame count and make the next poll (and this click) report frozen
    // while LIVE is still showing.
    const videoVerified = isVideoLiveNow();

    if (
      shouldReportNoVideo({
        videoVerified,
        waitExpired: waitExpiredRef.current,
        alreadyReported: noVideoReportedRef.current
      })
    ) {
      noVideoReportedRef.current = true;
      // On the failure event, never on a success name. This once rode the
      // first-stream event with an `outcome` property, which meant a camera
      // that produced NO video incremented the success count — inflating the
      // happy-path metric with exactly the population it exists to exclude.
      // ENG-4305's `onboarding_failed`, which classifies the raw error itself
      // and stamps severity, recoverability and the error-cohort keys. Terminal:
      // the wait expired with no frame and this screen is the end of the road.
      trackerRef.current.trackFailed({
        context: onboardingContextFor('magic-moment'),
        rawError: 'no video received from the camera',
        terminal: true
      });
    }

    onCompleteRef.current({ videoVerified });
  }, [isVideoLiveNow]);

  const handleMove = useCallback(
    (intent: PTZ) => {
      move(deviceId, intent, { toolUsed: 'dpad' });

      if (!isRealMovement(intent)) return;
      if (hasSentMovement) return;

      setHasSentMovement(true);
      setCelebrating(true);
      // NO first-PTZ event. ENG-4305 supersedes it: first-ness is "the first
      // move event for a device_id after its device_add", and `move()` below
      // already emits `device_move_direct` through
      // control/movement/commands/move-to.ts. This one IS recoverable today —
      // unlike first-stream and the discovery count, the underlying event
      // really does fire on this path.

      celebrationTimerRef.current = setTimeout(() => {
        setCelebrating(false);
        completionTimerRef.current = setTimeout(() => finish(), 1_200);
      }, 2_000);
    },
    [deviceId, hasSentMovement, finish]
  );

  // Courtesy auto-advance for a user who watched their camera and never touched
  // the pad. It advances ONLY on live video — with no frame the window ends in
  // the failure panel below, because an auto-advance to "You're all set" over a
  // spinner tells the user they succeeded while they are looking at nothing.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isVideoLiveNow()) {
        finish();
        return;
      }
      setWaitExpired(true);
    }, MAGIC_MOMENT_FALLBACK_MS);

    return () => clearTimeout(timer);
  }, [finish, isVideoLiveNow, retrySignal]);

  useEffect(
    () => () => {
      if (celebrationTimerRef.current !== undefined) clearTimeout(celebrationTimerRef.current);
      if (completionTimerRef.current !== undefined) clearTimeout(completionTimerRef.current);
    },
    []
  );

  const handleRetry = useCallback((): void => {
    samplerRef.current = createPlaybackSampler();
    setVideoReady(false);
    setWaitExpired(false);
    setRetrySignal((count) => count + 1);
  }, []);

  const videoFailed = isVideoFailed({ waitExpired, videoReady });
  const coachMark = selectCoachMark({
    videoFailed,
    hasSentMovement,
    celebrating,
    controllable,
    padEnabled: videoReady
  });

  return (
    <FunnelShell announceTitle={config.name || 'Your camera'} onEscape={finish} progressStep={5}>
      {/* Single root so FunnelShell's stagger wrapper cannot break the internal
          grid below. */}
      <div className='flex w-full max-w-[40rem] flex-col'>
        <div className='flex w-full items-start justify-between text-left'>
          <div>
            <h1 className='text-2xl font-demibold text-white'>{config.name || 'Your camera'}</h1>
            <p className='mt-4 text-sm text-muted'>
              {videoFailed ? 'Camera added' : 'Camera connected'} &bull;{' '}
              <span className='font-mono'>{config.host}</span>
            </p>
          </div>
          {/* Hidden when the pad is absent: the primary Continue below is then
              the only action, and two buttons doing one thing makes neither
              read as the way forward. */}
          {controllable && (
            <Button
              {...STagNew(Sel.OnboardingMagicMoment, 'complete')}
              className='text-foreground'
              onClick={finish}
              size='md'
              variant='secondary'
            >
              {videoFailed ? 'Continue to Studio' : 'Done'}
            </Button>
          )}
        </div>

        {/* Viewer always mounts — mounting is what triggers the LiveKit
            subscription. Everything else overlays it. */}
        <div
          {...STagNew(Sel.OnboardingMagicMoment, 'viewer', undefined, STagType.Data)}
          className='relative mt-16 h-[240px] max-h-[240px] w-full overflow-hidden rounded-12 border border-base-100'
          ref={videoContainerRef}
        >
          <div className='absolute inset-0'>
            <Viewer deviceId={deviceId} key={retrySignal} />
          </div>

          {!videoReady && !videoFailed && (
            <div
              {...STagNew(Sel.OnboardingMagicMoment, 'connecting', undefined, STagType.Data)}
              className='absolute inset-0 flex flex-col items-center justify-center bg-base-300'
            >
              <Spinner className='text-primary' />
              <p className='mt-8 text-sm text-muted'>Connecting to camera…</p>
            </div>
          )}

          {/* The camera is added and kept either way, so the exit leads on to
              Studio — but nothing here claims the stream worked. */}
          {videoFailed && (
            <div
              {...STagNew(Sel.OnboardingMagicMoment, 'no_video', undefined, STagType.Data)}
              className='absolute inset-0 flex flex-col items-center justify-center bg-base-300 px-24 text-center'
              role='status'
            >
              <p className='text-sm font-medium text-white'>No video from this camera</p>
              <p className='mt-8 text-xs text-muted'>
                It was added to your sources, but no video arrived. Check the camera&apos;s stream
                path and codec, then try again.
              </p>
              <Button
                {...STagNew(Sel.OnboardingMagicMoment, 'retry_video')}
                className='mt-12 text-foreground'
                onClick={handleRetry}
                size='sm'
                variant='secondary'
              >
                Try again
              </Button>
            </div>
          )}

          {videoReady && (
            <div
              {...STagNew(Sel.OnboardingMagicMoment, 'live', undefined, STagType.Data)}
              className='bg-dark/60 absolute left-12 top-12 flex items-center gap-6 rounded-6 px-8 py-4 backdrop-blur'
            >
              <span className='h-6 w-6 animate-pulse rounded-full bg-error' />
              {/* Solid text-white, not text-white/80: the opacity variant is
                  used nowhere else, so Tailwind's JIT never emits it and it
                  falls back to black — invisible on this badge. */}
              <span className='font-semibold text-[10px] uppercase tracking-wider text-white'>
                Live
              </span>
            </div>
          )}

          {/* The movement API returns no acknowledgement, so this claims only
              what we can prove: the command left. */}
          {celebrating && (
            <div className='absolute inset-0 z-10 flex items-center justify-center'>
              <div className='bg-dark/80 rounded-8 px-24 py-12'>
                <p className='text-lg font-demibold text-success'>Control command sent</p>
              </div>
            </div>
          )}
        </div>

        {/* text-left resets FunnelShell's inherited text-center, which shifts
            the inline SVG content inside the D-pad's buttons. */}
        <div className='mt-20 flex w-full items-end gap-20 text-left'>
          <div className='flex-1'>
            {coachMark === 'locked' && (
              <span className='border-error/40 inline-flex items-center gap-6 rounded-full border bg-error/10 px-10 py-4 text-xs font-medium text-error'>
                Controls stay locked until video arrives
              </span>
            )}
            {coachMark === 'invite' && (
              <span className='border-primary/40 inline-flex items-center gap-6 rounded-full border bg-primary/10 px-10 py-4 text-xs font-medium text-primary'>
                <PointerIcon /> Try it — use the pad to pan and tilt
              </span>
            )}
            {coachMark === 'sent' && (
              <span className='border-success/40 inline-flex items-center gap-6 rounded-full border bg-success/10 px-10 py-4 text-xs font-medium text-success'>
                <CheckIcon /> Command sent — confirm the camera moved in the video.
              </span>
            )}
          </div>
          {/* The pad is omitted entirely for a camera with no controls — a
              disabled pad still advertises a capability this camera does not
              have. A primary Continue takes its place so the screen has an
              obvious action instead of an empty corner and a lone Done in the
              header. */}
          {controllable ? (
            <DPad
              allowZoom
              canPan
              canTilt
              className='w-[190px]'
              deviceId={deviceId}
              disabled={!videoReady}
              onMove={handleMove}
            />
          ) : (
            /* A camera with no control ends HERE. The "You're all set" screen
               adds a tick and a sentence the user already has, and takes their
               live camera off screen to do it — so this screen carries the
               final action instead (Aleksey, 2026-08-25). With a pad, the
               journey still ends on that screen, where it is the reward for
               actually moving the camera. */
            <Button
              {...STagNew(Sel.OnboardingMagicMoment, 'continue')}
              onClick={finish}
              size='lg'
              variant='primary'
            >
              {videoFailed ? 'Continue' : `Start using ${vendorName()}`}
            </Button>
          )}
        </div>
      </div>
    </FunnelShell>
  );
};

const PointerIcon = (): ReactElement => (
  <svg className='shrink-0' fill='none' height='14' viewBox='0 0 24 24' width='14'>
    <path
      d='M9 4l8 8-3 1-1 3-4-12z'
      fill='currentColor'
      stroke='currentColor'
      strokeLinejoin='round'
      strokeWidth='0.5'
    />
  </svg>
);

const CheckIcon = (): ReactElement => (
  <svg className='shrink-0' fill='none' height='14' viewBox='0 0 24 24' width='14'>
    <path
      d='M5 12l4 4 10-10'
      stroke='currentColor'
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth='2.6'
    />
  </svg>
);
